import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import BrowzwearAPIForm, { BrowzwearCredentials } from "components/BrowzwearAPIForm";
import browzwearService from "api/browzwear/browzwear.service";
import { useAppSelector, useAppDispatch } from "store";
import { setBrowzwear } from "store/commonSlice";
import { useSnackbar } from "hooks/useSnackbar";
import IntegrationCard from "pages/IntegrationsPage/IntegrationCard";
import useBrowzwearCardProps from "pages/IntegrationsPage/useBrowzwearCardProps";

/**
 * Account → Connected Apps → Browzwear: the connect/save card. Moved here VERBATIM from the old Account `Integrations` (its state, its
 * `browzwearService.connect` call, its `_handled` / empty-owner guards and toasts are unchanged) so the shared Integrations hub can
 * render it; Browzwear behaviour does not depend on where the card is shown.
 */
const BrowzwearConnectCard = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const { browzwear, user } = useAppSelector((state) => state.common);
  const [credentials, setCredentials] = useState<BrowzwearCredentials>({
    apiKey: browzwear.apiKey,
    apiSecret: browzwear.apiSecret,
    storeName: browzwear.storeName,
    email: browzwear.email || user.email || "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const cardProps = useBrowzwearCardProps();

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      const connectResponse = await browzwearService.connect({
        api_key: credentials.apiKey,
        api_secret: credentials.apiSecret,
        store_name: credentials.storeName,
        email: credentials.email,
      });

      // A resolved response is not a success — 401/403/409/503 resolve with `_handled: true` here, so
      // `catch` never fires and the old code saved `isConnected: true` with an empty owner_id, which
      // then went out on every export (REF-6494). The interceptor already showed the UX.
      if (connectResponse?._handled) return;

      const ownerId = connectResponse.responseData?.owner_id ?? "";

      // A 200 with no owner_id means the email lookup matched nothing; connecting anyway is the same
      // silent failure one step later.
      if (!ownerId) {
        showError(t("account:integrations.toasts.error"));

        return;
      }

      dispatch(
        setBrowzwear({
          apiKey: credentials.apiKey,
          apiSecret: credentials.apiSecret,
          storeName: credentials.storeName,
          email: credentials.email,
          ownerId,
          isConnected: true,
        })
      );

      showSuccess(t("account:integrations.toasts.success"));
    } catch {
      showError(t("account:integrations.toasts.error"));
    } finally {
      setIsSaving(false);
    }
  }, [credentials, dispatch, showSuccess, showError, t]);

  const isSaveDisabled = !credentials.apiKey || !credentials.apiSecret || !credentials.storeName || isSaving;

  return (
    <IntegrationCard
      data-testid="bw-connect-card"
      {...cardProps}
      bodyClassName="[&>div]:p-0"
      actions={
        <Button disabled={isSaveDisabled} className="btn-primary-small" onClick={handleSave}>
          {isSaving ? t("assets:assets.integrations.connecting") : t("assets:assets.integrations.connect_button")}
        </Button>
      }
    >
      <BrowzwearAPIForm credentials={credentials} setCredentials={setCredentials} />
    </IntegrationCard>
  );
};

export default BrowzwearConnectCard;
